import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, Redirect } from "react-router-dom";
import axios from "axios";
import {
  FaRoute,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaMapMarkerAlt,
  FaRoad,
  FaChevronRight,
  FaArrowRight,
  FaEdit,
} from "react-icons/fa";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import COLORS from "../resources/colors/COLORS";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import MdlAddTransportRoute from "../dialog/MdlAddTransportRoute";
import MdlEditTransportRoute from "../dialog/MdlEditTransportRoute";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return Math.round(n).toLocaleString("en-IN");
}

function formatDistance(raw) {
  if (raw == null || raw === "") return null;
  const value = String(raw).trim();
  if (!value) return null;
  if (/km/i.test(value)) return value;
  const n = Number(value);
  if (Number.isFinite(n)) return `${n} km`;
  return value;
}

function stopCount(route) {
  if (Array.isArray(route?.pickup_points)) return route.pickup_points.length;
  const n = Number(route?.stop_count ?? route?.pickup_point_count);
  return Number.isFinite(n) ? n : 0;
}

const RouteCard = memo(function RouteCard({ route, index, onEdit }) {
  const distance = formatDistance(route?.route_distance);
  const stops = stopCount(route);
  const pathLabel = `${route?.start_from || "—"} → ${route?.end_to || "—"}`;

  return (
    <article className="tr-card" style={{ "--tr-i": index }}>
      <Link
        to={`/AdminMain/TransportRoutesDetails/${encodeURIComponent(
          String(route.route_id)
        )}`}
        className="tr-card__head"
        aria-label={`View ${route.route_name || "route"} details`}
      >
        <span className="tr-card__index" aria-hidden>
          {String(index + 1).padStart(2, "0")}
        </span>

        <span className="tr-card__icon" aria-hidden>
          <FaRoute />
        </span>

        <div className="tr-card__main min-w-0">
          <div className="tr-card__top">
            <h3 className="tr-card__name">
              {route.route_name || "Unnamed route"}
            </h3>
            <p className="tr-card__path" title={pathLabel}>
              {route?.start_from || "—"}
              <FaArrowRight className="tr-card__path-arrow" aria-hidden />
              {route?.end_to || "—"}
            </p>
          </div>

          <div className="tr-card__chips">
            {distance ? (
              <span className="tr-chip tr-chip--distance">
                <FaRoad aria-hidden />
                {distance}
              </span>
            ) : null}
            <span
              className={`tr-chip tr-chip--stops${
                stops > 0 ? "" : " tr-chip--muted"
              }`}
            >
              <FaMapMarkerAlt aria-hidden />
              {stops} stop{stops === 1 ? "" : "s"}
            </span>
          </div>
        </div>
      </Link>

      <div className="tr-card__actions">
        <button
          type="button"
          className="tr-card__edit"
          onClick={() => onEdit?.(route)}
          title="Edit route"
          aria-label={`Edit ${route.route_name || "route"}`}
        >
          <FaEdit aria-hidden /> Edit
        </button>
        <Link
          to={`/AdminMain/TransportRoutesDetails/${encodeURIComponent(
            String(route.route_id)
          )}`}
          className="tr-card__go"
        >
          Details <FaChevronRight aria-hidden />
        </Link>
      </div>
    </article>
  );
});

const TransportRoutes = () => {
  const loggedIn = localStorage.getItem("token") != null;
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddRoute, setShowAddRoute] = useState(false);
  const [editRoute, setEditRoute] = useState(null);
  const abortRef = useRef(null);

  const loadRoutes = useCallback(async ({ soft = false } = {}) => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setRoutes([]);
      myToast.notify_warning("School code is missing.");
      setLoading(false);
      setRefreshing(false);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (soft) setRefreshing(true);
    else setLoading(true);

    try {
      const res = await axios.get(`${API_BASE()}getRouteList.php`, {
        params: { school_code: schoolCode },
        signal: controller.signal,
      });

      const payload = res?.data;
      if (payload?.api_status === "OK" && Array.isArray(payload.data)) {
        setRoutes(payload.data);
      } else {
        setRoutes([]);
        if (payload?.api_remark && payload.api_status !== "OK") {
          // Empty school is fine — only toast real errors
          if (!/no record/i.test(String(payload.api_remark))) {
            myToast.notify_warning(payload.api_remark);
          }
        }
      }
    } catch (error) {
      if (axios.isCancel?.(error) || error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
        return;
      }
      console.error("[TransportRoutes] load error", error);
      setRoutes([]);
      myToast.notify_warning("Network error loading routes.");
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    loadRoutes();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [loadRoutes]);

  const filteredRoutes = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return routes;
    return routes.filter((route) => {
      const pickupNames = Array.isArray(route.pickup_points)
        ? route.pickup_points.map((p) => p.pickup_point_name).filter(Boolean)
        : [];
      const hay = [
        route.route_name,
        route.start_from,
        route.end_to,
        route.route_distance,
        route.route_id,
        ...pickupNames,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [routes, searchTerm]);

  const totalStops = useMemo(
    () => routes.reduce((sum, route) => sum + stopCount(route), 0),
    [routes]
  );

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page tr-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Transport Routes</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaRoute />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Transport Routes</h1>
              <p className="sm-hero__sub">
                {formatCount(routes.length)} route
                {routes.length === 1 ? "" : "s"} · {formatCount(totalStops)}{" "}
                stop{totalStops === 1 ? "" : "s"}
              </p>
            </div>
          </div>

          <div className="sm-hero__tools">
            <div className="sm-hero__search-group">
              <FaSearch className="tr-search-icon" aria-hidden />
              <input
                type="search"
                className="sm-hero__search"
                placeholder="Search routes or stops…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search transport routes and pickup points"
              />
            </div>
            <button
              type="button"
              onClick={() => loadRoutes({ soft: true })}
              disabled={refreshing || loading}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh list"
              aria-label="Refresh list"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => setShowAddRoute(true)}
              className="tr-hero-link tr-hero-link--solid"
            >
              <FaPlus aria-hidden /> Add Route
            </button>
          </div>
        </header>

        <div className="tr-content">
          <div className="tr-section-head">
            <div className="min-w-0">
              <h2 className="tr-section-head__title">Active routes</h2>
              <p className="tr-section-head__sub">
                Open a route to manage stops and student details
              </p>
            </div>
            <span className="tr-section-head__count">
              {`${filteredRoutes.length}${
                searchTerm.trim() ? ` of ${routes.length}` : ""
              } route${filteredRoutes.length === 1 ? "" : "s"}`}
            </span>
          </div>

          {loading ? (
            <div className="tr-loading">
              <MyLoader />
            </div>
          ) : filteredRoutes.length === 0 ? (
            <div className="sm-empty tr-empty">
              <div className="tr-empty__circle" aria-hidden>
                <FaRoute />
              </div>
              <p className="sm-empty__title">
                {routes.length === 0
                  ? "No routes yet"
                  : "No matches for your search"}
              </p>
              <p className="sm-empty__hint">
                {routes.length === 0
                  ? "Create a route to start assigning pickup stops and students."
                  : "Try a different route name, start, end, or pickup point."}
              </p>
              {routes.length === 0 ? (
                <button
                  type="button"
                  className="tr-empty-cta"
                  onClick={() => setShowAddRoute(true)}
                >
                  <FaPlus aria-hidden /> Add New Route
                </button>
              ) : null}
            </div>
          ) : (
            <div className="tr-list" role="list">
              {filteredRoutes.map((route, index) => (
                <RouteCard
                  key={route.route_id || index}
                  route={route}
                  index={index}
                  onEdit={setEditRoute}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {showAddRoute ? (
        <MdlAddTransportRoute
          actionClose={() => setShowAddRoute(false)}
          onSuccess={() => loadRoutes({ soft: true })}
        />
      ) : null}

      {editRoute ? (
        <MdlEditTransportRoute
          route={editRoute}
          actionClose={() => setEditRoute(null)}
          onSuccess={() => loadRoutes({ soft: true })}
        />
      ) : null}
    </div>
  );
};

export default TransportRoutes;
